import { Link } from "react-router-dom";
import { Database, KeyRound, Radar, Bot, Inbox, CalendarCheck, Plane, CheckCircle2, ArrowRight } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { Reveal, Overline } from "../Reveal";

export function Marquee() {
  const { t } = useLang();
  const items = t("marquee");
  const row = [...items, ...items];
  return (
    <div className="relative bg-[#1B2A4E] py-4 overflow-hidden" data-testid="trust-marquee">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {row.map((item, i) => (
          <span key={i} className="mx-8 text-xs font-mono tracking-[0.2em] uppercase text-slate-300 flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E8842C] inline-block" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Welcome() {
  const { t, isRtl } = useLang();
  const cards = [
    { img: "/assets/bg-employers.jpg", title: t("welcome.emp_t"), desc: t("welcome.emp_d"), points: t("welcome.emp_points"), to: "/employer", testid: "welcome-employers" },
    { img: "/assets/bg-jobseekers.jpg", title: t("welcome.job_t"), desc: t("welcome.job_d"), points: t("welcome.job_points"), to: "/employee", testid: "welcome-jobseekers" },
  ];
  return (
    <section className="relative py-28 px-6 sm:px-12" data-testid="welcome-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <Overline>{t("welcome.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("welcome.title")}</h2>
          <p className="text-[#1B2A4E]/60 mt-4 text-lg leading-relaxed">{t("welcome.sub")}</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {cards.map((c, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div data-testid={c.testid} className="card-light overflow-hidden group h-full flex flex-col">
                <div className="relative h-44 overflow-hidden">
                  <img src={c.img} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E]/70 to-transparent" />
                  <h3 className="absolute bottom-4 start-5 font-head font-black text-2xl text-white">{c.title}</h3>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-sm text-[#1B2A4E]/65 leading-relaxed">{c.desc}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {c.points.map((p, j) => (
                      <li key={j} className="flex items-center gap-2.5 text-sm font-bold text-[#1B2A4E]">
                        <CheckCircle2 className="w-4 h-4 text-[#E8842C] shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link to={c.to} data-testid={`${c.testid}-cta`} className="mt-6 inline-flex items-center gap-2 text-sm font-black text-[#E8842C] hover:gap-3.5 transition-all">
                    {t("welcome.explore")} <ArrowRight className={`w-4 h-4 ${isRtl ? "rotate-180" : ""}`} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Manifesto() {
  const { t } = useLang();
  const chapters = [
    { n: "01", t: t("manifesto.c1t"), d: t("manifesto.c1d") },
    { n: "02", t: t("manifesto.c2t"), d: t("manifesto.c2d") },
    { n: "03", t: t("manifesto.c3t"), d: t("manifesto.c3d") },
    { n: "04", t: t("manifesto.c4t"), d: t("manifesto.c4d") },
  ];
  return (
    <section className="relative py-28 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Overline>{t("manifesto.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E] mb-16">{t("manifesto.title")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1}>
              <div
                data-testid={`manifesto-chapter-${c.n}`}
                className={`group relative rounded-3xl border border-[#1B2A4E]/10 p-8 sm:p-10 overflow-hidden transition-all duration-500 hover:border-[#E8842C]/60 hover:shadow-xl hover:shadow-[#E8842C]/10 ${
                  i % 2 === 1 ? "md:translate-y-10 bg-[#FAF7F2]" : "bg-white"
                }`}
              >
                <span className="font-head font-black text-7xl text-[#1B2A4E]/5 absolute -top-2 end-6 group-hover:text-[#E8842C]/15 transition-colors duration-500">{c.n}</span>
                <span className="text-xs font-mono tracking-[0.3em] text-[#E8842C] font-bold">{c.n}</span>
                <h3 className="font-head font-bold text-xl sm:text-2xl text-[#1B2A4E] mt-3 mb-4">{c.t}</h3>
                <p className="text-sm text-[#1B2A4E]/60 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const TECH_ICONS = [Database, KeyRound, Radar, Bot];

export function Technologies() {
  const { t } = useLang();
  const items = t("tech.items");
  return (
    <section className="relative py-28 px-6 sm:px-12 bg-[#1B2A4E] dot-grid" data-testid="tech-section">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <Overline>{t("tech.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-white">{t("tech.title")}</h2>
          <p className="text-slate-300/80 mt-4 text-lg leading-relaxed">{t("tech.sub")}</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {items.map((item, i) => {
            const Icon = TECH_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`tech-item-${i}`} className="group rounded-2xl border border-white/10 bg-white/5 p-7 h-full hover:border-[#E8842C]/60 hover:bg-white/10 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#E8842C] flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs font-mono text-[#F5A623] font-bold">0{i + 1}</span>
                  <h3 className="font-head font-bold text-white mt-2 mb-3 leading-snug">{item.t}</h3>
                  <p className="text-sm text-slate-300/70 leading-relaxed">{item.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const HOW_ICONS = [Inbox, CalendarCheck, Plane];

export function HowItWorks() {
  const { t } = useLang();
  const steps = t("how.steps");
  return (
    <section className="relative py-28 px-6 sm:px-12" data-testid="how-section">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Overline>{t("how.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("how.title")}</h2>
          <p className="text-[#1B2A4E]/60 mt-4">{t("how.sub")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14 relative">
          <div className="hidden md:block absolute top-10 inset-x-24 h-px border-t-2 border-dashed border-[#E8842C]/40" />
          {steps.map((s, i) => {
            const Icon = HOW_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.12}>
                <div data-testid={`how-step-${i}`} className="relative text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-white border-2 border-[#E8842C] flex items-center justify-center shadow-lg shadow-[#E8842C]/20 relative z-10">
                    <Icon className="w-8 h-8 text-[#E8842C]" />
                    <span className="absolute -top-2 -end-2 w-7 h-7 rounded-full bg-[#1B2A4E] text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                  </div>
                  <h3 className="font-head font-bold text-lg text-[#1B2A4E] mt-6 mb-3">{s.t}</h3>
                  <p className="text-sm text-[#1B2A4E]/60 leading-relaxed max-w-xs mx-auto">{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Process() {
  const { t } = useLang();
  const steps = t("process.steps");
  return (
    <section className="relative py-28 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid="process-section">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <Overline>{t("process.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E] mb-16">{t("process.title")}</h2>
        </Reveal>
        <div className="relative">
          <div className="absolute top-0 bottom-0 start-6 w-px bg-gradient-to-b from-[#E8842C] via-[#1B2A4E]/15 to-transparent" />
          <div className="space-y-10">
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-6 items-start group" data-testid={`process-step-${i + 1}`}>
                  <div className="relative z-10 w-12 h-12 shrink-0 rounded-full bg-white border-2 border-[#1B2A4E]/15 flex items-center justify-center font-head font-black text-[#E8842C] group-hover:bg-[#E8842C] group-hover:border-[#E8842C] group-hover:text-white group-hover:scale-110 transition-all duration-300">
                    {i + 1}
                  </div>
                  <div className="pt-1.5">
                    <h3 className="font-head font-bold text-lg text-[#1B2A4E]">{s.t}</h3>
                    <p className="text-sm text-[#1B2A4E]/60 mt-1 leading-relaxed max-w-xl">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
